/**
 * The ONLY renderer of model-authored prose on this surface.
 *
 * `generated-text.schema.json` x-rendering makes this binding: no screen
 * interpolates a `template` inline, because that is what lets check-contract
 * tell keyed copy from generated copy. A screen hands this component a
 * `GeneratedText | null` and a `copyKey`, and gets a sentence either way.
 *
 * Resolution and the client half of x-validation live in lib/generatedText.ts,
 * which is plain TypeScript with no React in it so the gate tests in
 * scripts/check-generated-text.mjs can drive it directly. This file is only the
 * rendering half, and it holds three rules:
 *
 *   fallback      Null, a locale whose base language differs, a refused gate or
 *                 an interpolation miss all render `copyKey` — keyed,
 *                 translated, correct. The slot is never blank and never shows
 *                 an error.
 *   noBadge       No "written by AI" badge, sparkle or disclaimer. Per
 *                 CLAUDE.md the interface labels the control and does not
 *                 narrate the mechanism; provenance lives on **privacy**, once.
 *   noLayoutJump  Both branches emit the same <p> with the same classes in the
 *                 same place, so a screen in `analysisPending` does not resize
 *                 when prose lands. The fallback is not a placeholder — it is
 *                 the finished screen, which prose may later improve on.
 *
 * Money, dates and percentages are formatted from the device locale, the same
 * as keyed copy — the payload carries typed params, never formatted text.
 */
import type { CSSProperties, ReactNode } from 'react'
import type { GeneratedText as GeneratedTextPayload } from '@/generated/types'
import { t, type CopyKey, type CopyParams } from '@/lib/copy'
import { displayLocale } from '@/lib/format'
import { resolveGeneratedText } from '@/lib/generatedText'
import { bridge } from '@/lib/bridge'
import { StepList } from './primitives'

export interface GeneratedTextProps {
  /** The payload, or null whenever generation is unavailable, pending or failed. */
  text?: GeneratedTextPayload | null
  /** Required. The translated sentence shown whenever `text` cannot be rendered. */
  copyKey: CopyKey
  /** Params for the copyKey fallback — the keyed template's own, not the model's. */
  params?: CopyParams
  /**
   * A deterministic list that outranks `copyKey` when the model wrote no
   * bullets — `recommendation.steps`, per recommendation.schema.json. When both
   * this and the prose are empty the caller does not render the slot at all, so
   * `copyKey` is the floor here rather than the usual answer.
   */
  lines?: string[]
  /**
   * The element the sentence renders as. `span` exists because some slots sit
   * inside a row `<button>`, where a `<p>` is invalid content — it is a nesting
   * choice, not a styling one, and both branches still share it.
   */
  as?: 'p' | 'span'
  className?: string
  style?: CSSProperties
}

export function GeneratedText({
  text,
  copyKey,
  params,
  lines,
  as: Tag = 'p',
  className,
  style,
}: GeneratedTextProps): ReactNode {
  const resolved = resolveGeneratedText(text, displayLocale, (gate) => {
    // Counted and logged, never surfaced — x-validation. The user sees the
    // keyed sentence and has no way to tell anything was discarded.
    bridge.log(`generatedText: discarded by ${gate}, rendering "${copyKey}"`)
  })

  const steps = <StepList steps={lines ?? []} />

  // The deterministic list outranks the copyKey when the model wrote nothing:
  // `recommendation.steps` is a better answer than any single sentence, and it
  // is what recommendation.schema.json names as the fallback for `prose.steps`.
  if (!resolved && lines?.length) return steps

  // One shape otherwise: the same element, with the same classes, in the same
  // place, in both branches. That is what x-rendering noLayoutJump asks for.
  return (
    <>
      <Tag className={className} style={style}>
        {resolved ? resolved.text : t(copyKey, params)}
      </Tag>
      {resolved?.bullets.length ? (
        <ol className="step-list">
          {resolved.bullets.map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
        </ol>
      ) : null}
    </>
  )
}
